/**
 * Step accessibility for `WizardSlidePanel` — who may enter which step, and what
 * has to pass first.
 *
 * Extracted into its own module because the rule has two enforcers that must
 * never disagree: `WizardStepNav` (which renders it — the `disabled` attribute
 * and the status class) and `WizardInner.jumpToStep` (which enforces it — the
 * dispatch). They used to carry independent copies, so a nav item that looked
 * clickable but whose click was swallowed — or the reverse — was one edit away
 * at all times. Both now call `resolveWizardStepAccess` and nothing else.
 */
import {
  type WizardStep,
  type WizardStepNavMode,
  type WizardStepStatus,
} from "./types";

// ─── Status helpers ───────────────────────────────────────────────────────────

/**
 * Status from the built-in heuristic, used when the consumer does not manage
 * `completedSteps` itself.
 *
 * Under `"validatedForward"` an un-reached step reports "available" rather than
 * "locked": the sidebar really can take the user there, and the locked styling
 * (50% opacity, not-allowed cursor — see `slide-panel.scss`) would lie about it.
 *
 * Note the known wart in the "complete" arm: it treats an *absent*
 * `stepValidity` entry as valid, and the wizard only writes `true` on a
 * successful Continue/Finish. So every step behind the current one reports
 * complete — including ones a forward jump skipped over without validating.
 * (Consumers may write `false` themselves: `IdentityStrategyStep` and
 * `CreatePolicyDeployStep` both dispatch a computed boolean, and that is
 * honoured here.) Cosmetic only: those steps stay backward-clickable regardless,
 * and a consumer that gates its save on a step union (as the Access Policy
 * wizard does on `review`) still refuses to submit a half-built form.
 */
export function getDefaultStepStatus(
  step: number,
  currentStep: number,
  stepValidity: Record<number, boolean>,
  navMode: WizardStepNavMode = "backwardOnly",
): WizardStepStatus {
  if (step === currentStep) return "active";
  if (step < currentStep && stepValidity[step] !== false) return "complete";
  return navMode === "validatedForward" ? "available" : "locked";
}

/** Status from consumer-managed completion flags plus AND-prerequisites. */
export function getKeyedStepStatus(
  key: string,
  isCurrentStep: boolean,
  completedSteps: Record<string, boolean>,
  deps: string[],
): WizardStepStatus {
  if (isCurrentStep) return "active";
  if (completedSteps[key]) return "complete";
  if (deps.every(d => completedSteps[d])) return "available";
  return "locked";
}

// ─── Access resolution ────────────────────────────────────────────────────────

/** Why a step cannot be entered from the nav. Each maps 1:1 to an i18n key. */
export type WizardStepBlockedReason =
  | "backNavigationLocked"
  | "footerOnlyEntry"
  | "notAvailableYet";

export interface WizardStepAccess {
  /** Drives the sidebar modifier class and the completed check icon. */
  status: WizardStepStatus;
  /** Whether a nav click on this step should be honored at all. */
  canEnter: boolean;
  /**
   * Whether honoring the click must first validate the step being LEFT. True
   * only for forward clicks under `"validatedForward"` — `completedSteps`
   * consumers keep their existing unvalidated forward jumps.
   */
  requiresValidation: boolean;
  /** Set only when `canEnter` is false. */
  blockedReason?: WizardStepBlockedReason;
}

export interface ResolveWizardStepAccessArgs {
  index: number;
  step: WizardStep;
  currentStep: number;
  stepValidity: Record<number, boolean>;
  completedSteps?: Record<string, boolean>;
  stepDependencies?: Record<string, string[]>;
  lockBackNavigation?: boolean;
  navMode?: WizardStepNavMode;
}

/**
 * The step-accessibility decision, in one place.
 *
 * Returns a descriptor rather than a bare boolean because three separate facts
 * come out of the same decision: the nav needs `status` (class + check icon) and
 * `blockedReason` (tooltip copy), while `jumpToStep` needs `canEnter` and
 * `requiresValidation`. Computing them together is what keeps the rendered state
 * and the enforced state from drifting.
 *
 * Precedence, outermost first:
 *  1. the active step is always enterable — clicking it is a no-op, and it must
 *     never render disabled;
 *  2. `lockBackNavigation` — nothing but the active step;
 *  3. `WizardStep.enterViaFooterOnly` — never reachable from the nav; locked
 *     unless the user already completed it, which keeps its status;
 *  4. status — consumer-managed flags, or the built-in heuristic.
 *
 * With `navMode` left at `"backwardOnly"` and no `enterViaFooterOnly` step, 1/2/4
 * reproduce the pre-existing behavior exactly.
 */
export function resolveWizardStepAccess({
  index,
  step,
  currentStep,
  stepValidity,
  completedSteps,
  stepDependencies,
  lockBackNavigation = false,
  navMode = "backwardOnly",
}: ResolveWizardStepAccessArgs): WizardStepAccess {
  const isActive = index === currentStep;
  const status = completedSteps
    ? getKeyedStepStatus(
        step.key,
        isActive,
        completedSteps,
        stepDependencies?.[step.key] ?? [],
      )
    : getDefaultStepStatus(index, currentStep, stepValidity, navMode);

  if (isActive) return { status, canEnter: true, requiresValidation: false };

  if (lockBackNavigation) {
    return {
      status,
      canEnter: false,
      requiresValidation: false,
      blockedReason: "backNavigationLocked",
    };
  }

  if (step.enterViaFooterOnly) {
    // A step ahead of the user stays "locked" — "available" would promise a
    // click that does nothing. A completed one keeps its check: it is done, just
    // not re-enterable, and it should look the same from every later step.
    const isDone = status === "complete";
    return {
      status: isDone ? "complete" : "locked",
      canEnter: false,
      requiresValidation: false,
      blockedReason: isDone ? "backNavigationLocked" : "footerOnlyEntry",
    };
  }

  if (status !== "complete" && status !== "available") {
    return {
      status,
      canEnter: false,
      requiresValidation: false,
      blockedReason: "notAvailableYet",
    };
  }

  return {
    status,
    canEnter: true,
    // Backward clicks are always free; only "validatedForward" gates forward
    // ones. Deliberately NOT applied to `completedSteps` consumers by default:
    // their forward jumps (the MCP Registry's "skip to review") are unvalidated
    // today and stay that way unless they opt into the mode.
    requiresValidation: navMode === "validatedForward" && index > currentStep,
  };
}
